import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const navItems = [
  { label: '首页', section: '' },
  { label: '分集剧情', section: 'episode' },
  { label: '剧情梳理', section: 'storyline' },
  { label: '常见问题', section: 'faq' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  const isHome = location.pathname === '/';
  const onHero = isHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location]);

  const goSection = (id: string) => {
    setOpen(false);
    if (!isHome) {
      navigate('/');
      window.setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }, 120);
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const term = query.trim();
    if (!term) return;
    navigate(`/?q=${encodeURIComponent(term)}`);
    setQuery('');
  };

  const solid = !onHero;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        solid ? 'bg-background-50/95 backdrop-blur border-b border-background-200' : 'bg-transparent'
      }`}
    >
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 md:px-6">
        {/* logo */}
        <a
          href="/"
          onClick={(e) => {
            e.preventDefault();
            navigate('/');
          }}
          className={`flex shrink-0 items-center gap-2 font-heading text-lg font-bold tracking-wide ${
            onHero ? 'text-background-50' : 'text-foreground-950'
          }`}
        >
          <span
            className={`flex h-9 w-9 items-center justify-center rounded-md ${
              onHero ? 'bg-background-50/20 text-accent-300' : 'bg-primary-500 text-background-50'
            }`}
          >
            <i className="ri-film-fill text-lg" />
          </span>
          羞羞动漫网
        </a>

        {/* desktop nav */}
        <div className="hidden items-center gap-1 md:flex">
          {navItems.map((item) => (
            <button
              key={item.label}
              type="button"
              onClick={() => (item.section ? goSection(item.section) : navigate('/'))}
              className={`whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-background-200/70 ${
                onHero ? 'text-background-50 hover:bg-background-50/15' : 'text-foreground-800 hover:bg-background-200/70'
              }`}
            >
              {item.label}
            </button>
          ))}
          <Link
            to="/about"
            className={`whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              onHero
                ? 'text-background-50 hover:bg-background-50/15'
                : 'text-foreground-800 hover:bg-background-200/70'
            }`}
          >
            关于我们
          </Link>
        </div>

        {/* search + cta */}
        <div className="flex items-center gap-2">
          <form
            onSubmit={handleSearch}
            className={`hidden items-center gap-2 rounded-full border px-3 py-1.5 lg:flex ${
              onHero ? 'border-background-50/40 bg-background-50/10' : 'border-background-300 bg-background-100'
            }`}
          >
            <i className={`ri-search-line text-sm ${onHero ? 'text-background-50/80' : 'text-foreground-500'}`} />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索想看的番剧…"
              className="w-36 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-500"
            />
          </form>
          <button
            type="button"
            onClick={() => navigate('/')}
            className={`hidden whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium transition-colors md:block ${
              onHero ? 'bg-accent-500 text-background-50 hover:bg-accent-600' : 'bg-primary-500 text-background-50 hover:bg-primary-600'
            }`}
          >
            开始补番
          </button>

          {/* hamburger */}
          <button
            type="button"
            aria-label="菜单"
            onClick={() => setOpen((v) => !v)}
            className={`flex h-9 w-9 items-center justify-center rounded-md md:hidden ${
              onHero ? 'text-background-50' : 'text-foreground-800'
            }`}
          >
            <i className={`text-xl ${open ? 'ri-close-line' : 'ri-menu-line'}`} />
          </button>
        </div>
      </nav>

      {/* mobile panel */}
      {open && (
        <div className="border-t border-background-200 bg-background-50 md:hidden">
          <div className="space-y-1 px-4 py-3">
            {navItems.map((item) => (
              <button
                key={item.label}
                type="button"
                onClick={() => (item.section ? goSection(item.section) : navigate('/'))}
                className="block w-full rounded-md px-3 py-2.5 text-left text-sm font-medium text-foreground-800 hover:bg-background-100"
              >
                {item.label}
              </button>
            ))}
            <Link
              to="/about"
              className="block w-full rounded-md px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-100"
            >
              关于我们
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}